import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { api } from '../api';
import { useWebContainer, type BootPhase } from '../hooks/useWebContainer';
import EditorTerminal from '../components/EditorTerminal';

// ============================================================
// Helpers
// ============================================================

function getLanguage(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescriptreact',
    js: 'javascript', jsx: 'javascriptreact',
    css: 'css', json: 'json', yaml: 'yaml', yml: 'yaml',
    md: 'markdown', html: 'html', toml: 'ini',
  };
  return map[ext] ?? 'plaintext';
}

function displayName(path: string): string {
  return path.split('/').pop() ?? path;
}

const PHASE_LABELS: Record<BootPhase, string> = {
  idle: 'Initializing...',
  fetching: 'Fetching project files...',
  booting: 'Booting WebContainer...',
  mounting: 'Mounting file system...',
  installing: 'Installing dependencies...',
  starting: 'Starting dev server...',
  ready: 'Ready',
  error: 'Error',
};

// ============================================================
// File Tree
// ============================================================

interface TreeNode {
  path: string;
  name: string;
  type: 'file' | 'dir';
  children?: TreeNode[];
  loaded: boolean;
}

interface FileTreeProps {
  influencerDbId: number;
  selectedPath: string;
  dirtyFiles: Set<string>;
  onSelect: (path: string) => void;
}

function FileTree({ influencerDbId, selectedPath, dirtyFiles, onSelect }: FileTreeProps) {
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['src']));
  const [loading, setLoading] = useState(true);

  const loadDir = useCallback(async (dirPath: string): Promise<TreeNode[]> => {
    try {
      const res = await api.listFiles(influencerDbId, dirPath);
      return res.entries
        .sort((a, b) => {
          if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
          return a.path.localeCompare(b.path);
        })
        .map((e) => ({
          path: e.path,
          name: displayName(e.path),
          type: e.type,
          loaded: e.type === 'file',
          children: e.type === 'dir' ? [] : undefined,
        }));
    } catch {
      return [];
    }
  }, [influencerDbId]);

  useEffect(() => {
    loadDir('').then((rootNodes) => {
      setNodes(rootNodes);
      setLoading(false);
    });
  }, [loadDir]);

  const toggleDir = async (node: TreeNode) => {
    const isExpanded = expanded.has(node.path);
    if (isExpanded) {
      setExpanded((prev) => { const s = new Set(prev); s.delete(node.path); return s; });
      return;
    }
    if (!node.loaded) {
      const children = await loadDir(node.path);
      setNodes((prev) => updateChildren(prev, node.path, children));
    }
    setExpanded((prev) => new Set([...prev, node.path]));
  };

  function updateChildren(nodes: TreeNode[], target: string, children: TreeNode[]): TreeNode[] {
    return nodes.map((n) => {
      if (n.path === target) return { ...n, children, loaded: true };
      if (n.children) return { ...n, children: updateChildren(n.children, target, children) };
      return n;
    });
  }

  function renderNode(node: TreeNode, depth: number = 0) {
    const indent = depth * 12;
    if (node.type === 'dir') {
      const isOpen = expanded.has(node.path);
      return (
        <div key={node.path}>
          <button
            onClick={() => toggleDir(node)}
            className="w-full flex items-center gap-1.5 px-2 py-1 text-sm text-ink-400 hover:text-ink-200 hover:bg-ink-800/50 rounded transition-colors text-left"
            style={{ paddingLeft: `${8 + indent}px` }}
          >
            <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <svg className="w-4 h-4 flex-shrink-0 text-amber-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            </svg>
            <span className="truncate">{node.name}</span>
          </button>
          {isOpen && node.children && (
            <div>{node.children.map((child) => renderNode(child, depth + 1))}</div>
          )}
        </div>
      );
    }

    const isSelected = selectedPath === node.path;
    const isDirty = dirtyFiles.has(node.path);
    return (
      <button
        key={node.path}
        onClick={() => onSelect(node.path)}
        className={`w-full flex items-center gap-1.5 px-2 py-1 text-sm rounded transition-colors text-left truncate ${
          isSelected
            ? 'bg-accent-500/20 text-accent-300'
            : 'text-ink-400 hover:text-ink-200 hover:bg-ink-800/50'
        }`}
        style={{ paddingLeft: `${8 + indent + 12}px` }}
        title={node.path}
      >
        <svg className="w-4 h-4 flex-shrink-0 text-ink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        <span className="truncate">{node.name}</span>
        {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 ml-auto" />}
      </button>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-20">
        <div className="w-5 h-5 border-2 border-ink-600 border-t-ink-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto py-1">
      {nodes.map((n) => renderNode(n))}
    </div>
  );
}

// ============================================================
// Unsaved Changes Modal
// ============================================================

function UnsavedModal({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-ink-900 border border-ink-700 rounded-xl p-6 w-full max-w-sm shadow-2xl">
        <h3 className="text-lg font-semibold text-ink-100 mb-2">Unsaved Changes</h3>
        <p className="text-sm text-ink-400 mb-6">
          You have unsaved changes that will be lost if you leave.
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={onLeave} className="px-4 py-2 text-sm rounded-lg bg-ink-800 text-ink-300 hover:bg-ink-700 transition-colors">
            Leave without saving
          </button>
          <button onClick={onStay} className="px-4 py-2 text-sm rounded-lg bg-accent-500 text-white hover:bg-accent-400 transition-colors font-medium">
            Stay
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Open in Cursor Dropdown
// ============================================================

function OpenInCursorDropdown({ githubRepoUrl }: { githubRepoUrl: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const repoName = githubRepoUrl.split('/').pop() ?? '';
  const cloneCmd = `git clone ${githubRepoUrl}.git && cd ${repoName} && npm install`;
  const cursorLink = `cursor://file/~/projects/${repoName}`;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-ink-800 text-ink-300 hover:bg-ink-700 transition-colors font-medium"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
        Open in Cursor
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-ink-900 border border-ink-700 rounded-xl shadow-2xl p-4 z-50 space-y-3">
          <div>
            <p className="text-xs text-ink-500 mb-1">Clone command</p>
            <div className="flex items-center gap-2 bg-ink-950 rounded-lg p-2">
              <code className="text-xs text-ink-300 font-mono flex-1 overflow-x-auto whitespace-nowrap">{cloneCmd}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(cloneCmd); }}
                className="flex-shrink-0 p-1 text-ink-500 hover:text-ink-200 transition-colors"
                title="Copy"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          <a
            href={cursorLink}
            className="block w-full text-center text-xs px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-medium transition-colors"
          >
            Open in Cursor
          </a>

          <p className="text-xs text-ink-600">
            For heavy refactoring or debugging. Run <code className="text-ink-400">npm run dev</code> locally for full HMR.
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Editor Page
// ============================================================

export default function AdminWebsiteEditor() {
  const { influencerDbId } = useParams();
  const navigate = useNavigate();
  const id = parseInt(influencerDbId!);

  // Site meta
  const [brandName, setBrandName] = useState('');
  const [githubRepoUrl, setGithubRepoUrl] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(true);

  // File state: maps path -> { content (original from GitHub), editorValue (live) }
  const [selectedFile, setSelectedFile] = useState('src/routes/home.tsx');
  const [fileContent, setFileContent] = useState('');
  const [editorValue, setEditorValue] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);

  // Dirty tracking
  const [dirtyFiles, setDirtyFiles] = useState<Set<string>>(new Set());
  const [fileBuffers, setFileBuffers] = useState<Map<string, { original: string; current: string }>>(new Map());
  const isDirty = dirtyFiles.size > 0;

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Navigation guard
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const pendingNavRef = useRef<string | null>(null);

  // AI panel
  const [aiInstruction, setAiInstruction] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  // Single-file mode
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [preAiContent, setPreAiContent] = useState('');
  // Multi-file mode
  const [aiEditMode, setAiEditMode] = useState<'single' | 'multi'>('multi');
  const [multiChanges, setMultiChanges] = useState<{ path: string; original: string; suggested: string }[]>([]);
  const [selectedDiffPath, setSelectedDiffPath] = useState<string | null>(null);

  // Bottom panel tab
  const [bottomTab, setBottomTab] = useState<'ai-changes' | 'terminal'>('terminal');

  // Right panel tab
  const [rightTab, setRightTab] = useState<'ai' | 'replicate'>('ai');

  // Replicate panel
  const [replicateUrl, setReplicateUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  const [scrapeResult, setScrapeResult] = useState<{
    url: string;
    extractedColors: string[];
    extractedFonts: string[];
    screenshot: string;
    navStyle: string;
    layoutSections: number;
  } | null>(null);

  // Terminal data handler
  const terminalWriteRef = useRef<((data: string) => void) | null>(null);

  const handleTerminalData = useCallback((data: string) => {
    terminalWriteRef.current?.(data);
  }, []);

  const setTerminalHandler = useCallback((handler: (data: string) => void) => {
    terminalWriteRef.current = handler;
  }, []);

  // WebContainer
  const { phase, error: wcError, serverUrl, writeFile } = useWebContainer({
    influencerDbId: id,
    onTerminalData: handleTerminalData,
  });

  // Editor value ref for closures
  const editorValueRef = useRef(editorValue);
  useEffect(() => { editorValueRef.current = editorValue; }, [editorValue]);

  // ── Load site meta ───────────────────────────────────────────

  useEffect(() => {
    api.getWebsiteDetail(id)
      .then((detail) => {
        setBrandName(detail.influencer.stage_name);
        setGithubRepoUrl(detail.site.github_repo_url ?? '');
      })
      .catch(() => {})
      .finally(() => setLoadingMeta(false));
  }, [id]);

  // ── Load file when selectedFile changes ──────────────────────

  const loadFile = useCallback(async (path: string) => {
    // If we have a buffer for this file, restore it instead of fetching
    const existing = fileBuffers.get(path);
    if (existing) {
      setFileContent(existing.original);
      setEditorValue(existing.current);
      setLoadingFile(false);
      return;
    }

    setLoadingFile(true);
    setAiSuggestion(null);
    setAiError('');
    try {
      const file = await api.getFile(id, path);
      setFileContent(file.content);
      setEditorValue(file.content);
      setFileBuffers((prev) => {
        const next = new Map(prev);
        next.set(path, { original: file.content, current: file.content });
        return next;
      });
    } catch (err) {
      console.error('Failed to load file:', err);
    } finally {
      setLoadingFile(false);
    }
  }, [id, fileBuffers]);

  useEffect(() => {
    loadFile(selectedFile);
  }, [selectedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle editor changes ────────────────────────────────────

  const handleEditorChange = useCallback((val: string | undefined) => {
    const value = val ?? '';
    setEditorValue(value);

    setFileBuffers((prev) => {
      const next = new Map(prev);
      const existing = next.get(selectedFile);
      next.set(selectedFile, { original: existing?.original ?? fileContent, current: value });
      return next;
    });

    const original = fileBuffers.get(selectedFile)?.original ?? fileContent;
    if (value !== original) {
      setDirtyFiles((prev) => new Set([...prev, selectedFile]));
    } else {
      setDirtyFiles((prev) => {
        const next = new Set(prev);
        next.delete(selectedFile);
        return next;
      });
    }

    writeFile(selectedFile, value);
  }, [selectedFile, fileContent, fileBuffers, writeFile]);

  // ── Save all dirty files ─────────────────────────────────────

  const handleSaveAll = async () => {
    if (dirtyFiles.size === 0) return;
    setIsSaving(true);
    setSaveMessage('');
    try {
      const files = Array.from(dirtyFiles).map((path) => ({
        path,
        content: fileBuffers.get(path)?.current ?? '',
      }));

      await api.commitFiles(id, {
        files,
        message: `edit: update ${files.length} file${files.length > 1 ? 's' : ''} via portal editor`,
      });

      // Reset dirty state, update originals
      setFileBuffers((prev) => {
        const next = new Map(prev);
        for (const f of files) {
          const existing = next.get(f.path);
          if (existing) {
            next.set(f.path, { original: f.content, current: f.content });
          }
        }
        return next;
      });
      setFileContent(editorValueRef.current);
      setDirtyFiles(new Set());
      setSaveMessage('Saved & deploying...');
      setTimeout(() => setSaveMessage(''), 4000);
    } catch (err) {
      setSaveMessage(err instanceof Error ? `Error: ${err.message}` : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  // ── AI edit ──────────────────────────────────────────────────

  const handleAiEdit = async () => {
    if (!aiInstruction.trim()) return;
    setIsAiLoading(true);
    setAiError('');
    setAiSuggestion(null);
    setMultiChanges([]);

    if (aiEditMode === 'multi') {
      try {
        const res = await api.aiEditMultiFile(id, { instruction: aiInstruction });
        if (res.changes.length === 0) {
          setAiError('AI made no changes. Try rephrasing your instruction.');
          return;
        }
        setMultiChanges(res.changes);
        setSelectedDiffPath(res.changes[0].path);
        setBottomTab('ai-changes');
      } catch (err) {
        setAiError(err instanceof Error ? err.message : 'AI edit failed');
      } finally {
        setIsAiLoading(false);
      }
      return;
    }

    // Single-file mode
    try {
      const res = await api.aiEditFile(id, {
        path: selectedFile,
        instruction: aiInstruction,
      });
      setPreAiContent(editorValueRef.current);
      setAiSuggestion(res.suggested);
      setBottomTab('ai-changes');
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI edit failed');
    } finally {
      setIsAiLoading(false);
    }
  };

  // Apply a single-file suggestion to the editor
  const applyFileSuggestion = (path: string, suggested: string) => {
    setFileBuffers((prev) => {
      const next = new Map(prev);
      const existing = next.get(path);
      next.set(path, { original: existing?.original ?? '', current: suggested });
      return next;
    });
    setDirtyFiles((prev) => new Set([...prev, path]));
    writeFile(path, suggested);
    // If this is the currently open file, update the editor view
    if (path === selectedFile) {
      setEditorValue(suggested);
    }
  };

  // Single-file accept
  const handleAcceptAi = () => {
    if (!aiSuggestion) return;
    applyFileSuggestion(selectedFile, aiSuggestion);
    setAiSuggestion(null);
    setAiInstruction('');
    setPreAiContent('');
  };

  const handleRejectAi = () => {
    setAiSuggestion(null);
    setAiInstruction('');
    setPreAiContent('');
  };

  // Multi-file accept one file
  const handleAcceptOneMulti = (path: string) => {
    const change = multiChanges.find((c) => c.path === path);
    if (!change) return;
    applyFileSuggestion(path, change.suggested);
    const remaining = multiChanges.filter((c) => c.path !== path);
    setMultiChanges(remaining);
    if (remaining.length > 0) {
      setSelectedDiffPath(remaining[0].path);
    } else {
      setAiInstruction('');
    }
  };

  // Multi-file reject one file
  const handleRejectOneMulti = (path: string) => {
    const remaining = multiChanges.filter((c) => c.path !== path);
    setMultiChanges(remaining);
    if (remaining.length > 0) {
      setSelectedDiffPath(remaining[0].path);
    } else {
      setAiInstruction('');
    }
  };

  // Multi-file accept all
  const handleAcceptAllMulti = () => {
    for (const change of multiChanges) {
      applyFileSuggestion(change.path, change.suggested);
    }
    setMultiChanges([]);
    setSelectedDiffPath(null);
    setAiInstruction('');
  };

  // Multi-file reject all
  const handleRejectAllMulti = () => {
    setMultiChanges([]);
    setSelectedDiffPath(null);
    setAiInstruction('');
  };

  // ── Scrape inspiration ────────────────────────────────────────

  const handleScrape = async () => {
    const urlTrimmed = replicateUrl.trim();
    if (!urlTrimmed) return;
    setIsScraping(true);
    setScrapeError('');
    setScrapeResult(null);
    try {
      const res = await api.scrapeInspiration(id, urlTrimmed);
      setScrapeResult({
        url: res.url,
        extractedColors: res.extractedColors,
        extractedFonts: res.extractedFonts,
        screenshot: res.screenshot,
        navStyle: res.navStyle,
        layoutSections: res.layoutSections,
      });
    } catch (err) {
      setScrapeError(err instanceof Error ? err.message : 'Scrape failed');
    } finally {
      setIsScraping(false);
    }
  };

  // ── Navigation guard ──────────────────────────────────────────

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleBack = () => {
    const target = `/admin/websites/${influencerDbId}`;
    if (isDirty) {
      pendingNavRef.current = target;
      setShowLeaveModal(true);
    } else {
      navigate(target);
    }
  };

  const handleFileSelect = (path: string) => {
    if (path === selectedFile) return;
    // Save current buffer before switching
    setFileBuffers((prev) => {
      const next = new Map(prev);
      const existing = next.get(selectedFile);
      next.set(selectedFile, { original: existing?.original ?? fileContent, current: editorValueRef.current });
      return next;
    });
    setAiSuggestion(null);
    setSelectedFile(path);
  };

  // ── Keyboard shortcut: Cmd+S / Ctrl+S ───────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && !isSaving) handleSaveAll();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isDirty, isSaving]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Preview overlay message ──────────────────────────────────

  const previewOverlay = useMemo(() => {
    if (phase === 'error') return { show: true, text: 'WebContainer error', sub: wcError ?? '' };
    if (phase === 'ready') return { show: false, text: '', sub: '' };
    return { show: true, text: PHASE_LABELS[phase], sub: phase === 'installing' ? 'This may take 20-40s' : '' };
  }, [phase, wcError]);

  // ── Loading gate ─────────────────────────────────────────────

  if (loadingMeta) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-ink-950">
        <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-ink-950 flex flex-col">
      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between px-4 py-2 bg-ink-950 border-b border-ink-800 flex-shrink-0" style={{ height: '40px' }}>
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={handleBack} className="p-1.5 text-ink-400 hover:text-ink-200 transition-colors rounded hover:bg-ink-800" title="Back to detail">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-medium text-ink-200 truncate">{brandName}</span>
          <span className="text-ink-600">/</span>
          <span className="text-sm text-ink-400 font-mono truncate">{selectedFile}</span>
          {isDirty && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title={`${dirtyFiles.size} unsaved file(s)`} />}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {saveMessage && (
            <span className={`text-xs ${saveMessage.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>
              {saveMessage}
            </span>
          )}

          {/* Phase indicator */}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            phase === 'ready'
              ? 'bg-emerald-500/10 text-emerald-400'
              : phase === 'error'
                ? 'bg-red-500/10 text-red-400'
                : 'bg-violet-500/10 text-violet-400'
          }`}>
            {PHASE_LABELS[phase]}
          </span>

          {githubRepoUrl && (
            <a href={githubRepoUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-xs text-ink-400 hover:text-ink-200 rounded hover:bg-ink-800 transition-colors">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
              </svg>
              GitHub
            </a>
          )}

          {githubRepoUrl && <OpenInCursorDropdown githubRepoUrl={githubRepoUrl} />}

          <button
            onClick={handleSaveAll}
            disabled={!isDirty || isSaving}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-1.5 ${
              isDirty && !isSaving
                ? 'bg-accent-500 hover:bg-accent-400 text-white'
                : 'bg-ink-800 text-ink-500 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : (
              <>Save Changes {isDirty && <span className="bg-white/20 rounded-full px-1.5 text-[10px]">{dirtyFiles.size}</span>}</>
            )}
          </button>
        </div>
      </div>

      {/* ── Panel Body ── */}
      <Group orientation="horizontal" className="flex-1">
        {/* ── LEFT: Code Panel ── */}
        <Panel defaultSize={40} minSize={20}>
          <Group orientation="vertical">
            {/* Top: File tree + Monaco Editor */}
            <Panel defaultSize={65} minSize={25}>
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center px-3 py-1.5 border-b border-ink-800 bg-ink-950 flex-shrink-0">
                  <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Files</span>
                </div>
                <div className="h-44 border-b border-ink-800 bg-ink-950 overflow-y-auto flex-shrink-0">
                  <FileTree
                    influencerDbId={id}
                    selectedPath={selectedFile}
                    dirtyFiles={dirtyFiles}
                    onSelect={handleFileSelect}
                  />
                </div>

                {/* Monaco Editor */}
                <div className="flex-1 overflow-hidden relative">
                  {aiSuggestion !== null && (
                    <div className="absolute top-0 left-0 right-0 z-10 bg-violet-500/10 border-b border-violet-500/20 px-3 py-1.5 flex items-center gap-2">
                      <span className="text-xs text-violet-300 font-medium">AI suggestion pending — review below</span>
                    </div>
                  )}
                  {loadingFile && (
                    <div className="absolute inset-0 flex items-center justify-center bg-ink-950/80 z-10">
                      <div className="w-6 h-6 border-2 border-ink-600 border-t-ink-300 rounded-full animate-spin" />
                    </div>
                  )}
                  <Editor
                    height="100%"
                    language={getLanguage(selectedFile)}
                    value={editorValue}
                    onChange={handleEditorChange}
                    theme="vs-dark"
                    options={{
                      fontSize: 13,
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      renderWhitespace: 'selection',
                      padding: { top: aiSuggestion !== null ? 32 : 12 },
                      tabSize: 2,
                      readOnly: aiSuggestion !== null,
                    }}
                  />
                </div>
              </div>
            </Panel>

            <Separator className="h-1 bg-ink-800 hover:bg-violet-500/60 transition-colors cursor-row-resize" />

            {/* Bottom: AI Changes / Terminal tabs */}
            <Panel defaultSize={35} minSize={15}>
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center border-b border-ink-800 bg-ink-950 flex-shrink-0">
                  <button
                    onClick={() => setBottomTab('ai-changes')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      bottomTab === 'ai-changes'
                        ? 'text-violet-400 border-b-2 border-violet-500'
                        : 'text-ink-500 hover:text-ink-300'
                    }`}
                  >
                    AI Changes {(aiSuggestion !== null || multiChanges.length > 0) && (
                      <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-500/30 text-violet-300 text-xs font-bold">
                        {multiChanges.length > 0 ? multiChanges.length : 1}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setBottomTab('terminal')}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      bottomTab === 'terminal'
                        ? 'text-emerald-400 border-b-2 border-emerald-500'
                        : 'text-ink-500 hover:text-ink-300'
                    }`}
                  >
                    Terminal
                  </button>
                </div>

                <div className="flex-1 overflow-hidden">
                  {bottomTab === 'ai-changes' ? (
                    multiChanges.length > 0 ? (
                      /* ── Multi-file diff view ── */
                      <div className="flex flex-col h-full">
                        {/* Header: accept/reject all + file count */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-ink-900 border-b border-ink-800 flex-shrink-0">
                          <button
                            onClick={handleAcceptAllMulti}
                            className="text-xs px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
                          >
                            Accept All ({multiChanges.length})
                          </button>
                          <button
                            onClick={handleRejectAllMulti}
                            className="text-xs px-3 py-1 rounded bg-ink-700 hover:bg-ink-600 text-ink-300 font-medium transition-colors"
                          >
                            Reject All
                          </button>
                          <span className="text-xs text-ink-500 ml-auto">{multiChanges.length} file{multiChanges.length !== 1 ? 's' : ''} changed</span>
                        </div>
                        {/* File tabs */}
                        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-ink-800 bg-ink-950 flex-shrink-0 overflow-x-auto">
                          {multiChanges.map((c) => (
                            <button
                              key={c.path}
                              onClick={() => setSelectedDiffPath(c.path)}
                              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex-shrink-0 ${
                                selectedDiffPath === c.path
                                  ? 'bg-violet-600/30 text-violet-300 border border-violet-500/40'
                                  : 'text-ink-500 hover:text-ink-300 hover:bg-ink-800'
                              }`}
                            >
                              <span>{displayName(c.path)}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAcceptOneMulti(c.path); }}
                                className="text-emerald-400 hover:text-emerald-300 ml-1 font-bold"
                                title="Accept this file"
                              >✓</button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRejectOneMulti(c.path); }}
                                className="text-red-400 hover:text-red-300 font-bold"
                                title="Reject this file"
                              >✕</button>
                            </button>
                          ))}
                        </div>
                        {/* Diff for selected file */}
                        <div className="flex-1">
                          {selectedDiffPath && (() => {
                            const change = multiChanges.find((c) => c.path === selectedDiffPath);
                            return change ? (
                              <DiffEditor
                                key={selectedDiffPath}
                                original={change.original}
                                modified={change.suggested}
                                language={getLanguage(selectedDiffPath)}
                                theme="vs-dark"
                                options={{
                                  readOnly: true,
                                  renderSideBySide: false,
                                  fontSize: 13,
                                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                                  minimap: { enabled: false },
                                  scrollBeyondLastLine: false,
                                }}
                              />
                            ) : null;
                          })()}
                        </div>
                      </div>
                    ) : aiSuggestion !== null ? (
                      /* ── Single-file diff view ── */
                      <div className="flex flex-col h-full">
                        <div className="flex items-center gap-2 px-3 py-2 bg-ink-900 border-b border-ink-800 flex-shrink-0">
                          <button
                            onClick={handleAcceptAi}
                            className="text-xs px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={handleRejectAi}
                            className="text-xs px-3 py-1 rounded bg-ink-700 hover:bg-ink-600 text-ink-300 font-medium transition-colors"
                          >
                            Reject
                          </button>
                          <span className="text-xs text-ink-500 ml-auto">Diff for {displayName(selectedFile)}</span>
                        </div>
                        <div className="flex-1">
                          <DiffEditor
                            original={preAiContent}
                            modified={aiSuggestion}
                            language={getLanguage(selectedFile)}
                            theme="vs-dark"
                            options={{
                              readOnly: true,
                              renderSideBySide: false,
                              fontSize: 13,
                              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full text-ink-600 text-sm">
                        No AI changes — use the AI Edit panel to generate suggestions
                      </div>
                    )
                  ) : (
                    <EditorTerminal onData={setTerminalHandler} />
                  )}
                </div>
              </div>
            </Panel>
          </Group>
        </Panel>

        <Separator className="w-1 bg-ink-800 hover:bg-violet-500/60 transition-colors cursor-col-resize" />

        {/* ── CENTER: Preview iframe ── */}
        <Panel defaultSize={35} minSize={20}>
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-ink-800 bg-ink-950 flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider flex-shrink-0">Preview</span>
                {serverUrl && (
                  <span className="text-xs text-ink-600 truncate font-mono">{serverUrl}</span>
                )}
              </div>
            </div>

            <div className="flex-1 relative bg-white">
              {previewOverlay.show && (
                <div className="absolute inset-0 z-10 bg-ink-950 flex flex-col items-center justify-center gap-3">
                  {phase !== 'error' ? (
                    <div className="w-8 h-8 border-3 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
                  ) : (
                    <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  )}
                  <p className="text-sm text-violet-300 font-medium">{previewOverlay.text}</p>
                  {previewOverlay.sub && <p className="text-xs text-ink-500">{previewOverlay.sub}</p>}
                </div>
              )}

              {serverUrl && (
                <iframe
                  src={serverUrl}
                  className="w-full h-full border-0"
                  title="Site preview"
                  allow="cross-origin-isolated"
                />
              )}
            </div>
          </div>
        </Panel>

        <Separator className="w-1 bg-ink-800 hover:bg-violet-500/60 transition-colors cursor-col-resize" />

        {/* ── RIGHT: AI Prompt + Replicate ── */}
        <Panel defaultSize={25} minSize={15}>
          <div className="flex flex-col h-full overflow-hidden bg-ink-950">
            {/* Tab header */}
            <div className="flex border-b border-ink-800 flex-shrink-0">
              <button
                onClick={() => setRightTab('ai')}
                className={`flex-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  rightTab === 'ai'
                    ? 'text-accent-400 border-b-2 border-accent-500'
                    : 'text-ink-500 hover:text-ink-300'
                }`}
              >
                AI Edit
              </button>
              <button
                onClick={() => setRightTab('replicate')}
                className={`flex-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  rightTab === 'replicate'
                    ? 'text-violet-400 border-b-2 border-violet-500'
                    : 'text-ink-500 hover:text-ink-300'
                }`}
              >
                Replicate
              </button>
            </div>

            {/* ── AI Edit Tab ── */}
            {rightTab === 'ai' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">

                {/* Mode toggle */}
                <div className="flex rounded-lg overflow-hidden border border-ink-700">
                  <button
                    onClick={() => setAiEditMode('multi')}
                    className={`flex-1 text-xs py-1.5 font-medium transition-colors ${
                      aiEditMode === 'multi'
                        ? 'bg-violet-600 text-white'
                        : 'bg-ink-800 text-ink-400 hover:text-ink-300'
                    }`}
                  >
                    All Frontend
                  </button>
                  <button
                    onClick={() => setAiEditMode('single')}
                    className={`flex-1 text-xs py-1.5 font-medium transition-colors ${
                      aiEditMode === 'single'
                        ? 'bg-violet-600 text-white'
                        : 'bg-ink-800 text-ink-400 hover:text-ink-300'
                    }`}
                  >
                    This File
                  </button>
                </div>

                {/* Context indicator */}
                {aiEditMode === 'multi' ? (
                  <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-3 py-2">
                    <p className="text-xs text-violet-300 font-medium">Agent mode</p>
                    <p className="text-xs text-ink-500 mt-0.5">AI edits whichever files it needs — home, about, collection, product, search, cart, header, footer, CSS, and more.</p>
                  </div>
                ) : (
                  <div className="bg-ink-800/60 border border-ink-700 rounded-lg px-3 py-2">
                    <p className="text-xs text-ink-500 mb-1">Editing file</p>
                    <p className="text-xs font-mono text-accent-400 truncate">{selectedFile}</p>
                    <p className="text-xs text-ink-600 mt-1">Click any file in the left panel to switch target</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-ink-400 mb-2">
                    Instruction
                  </label>
                  <textarea
                    className="w-full bg-ink-800 border border-ink-700 rounded-lg p-3 text-sm text-ink-200 placeholder-ink-600 resize-none focus:outline-none focus:border-accent-500 transition-colors"
                    rows={5}
                    placeholder={
                      aiEditMode === 'multi'
                        ? `Describe what to change across the site...\n\nExamples:\n• Make all pages use a dark navy + gold palette\n• Add an announcement banner to every page\n• Redesign the product page layout`
                        : `Describe what to change in ${displayName(selectedFile)}...\n\nExamples:\n• Make the hero background a dark gradient\n• Add Instagram link to footer`
                    }
                    value={aiInstruction}
                    onChange={(e) => setAiInstruction(e.target.value)}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                        e.preventDefault();
                        if (!isAiLoading && aiInstruction.trim()) handleAiEdit();
                      }
                    }}
                  />
                  <p className="text-xs text-ink-600 mt-1">Cmd+Enter to apply</p>
                </div>

                {aiError && (
                  <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">
                    {aiError}
                  </p>
                )}

                <button
                  onClick={handleAiEdit}
                  disabled={isAiLoading || !aiInstruction.trim()}
                  className={`w-full text-sm py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    !isAiLoading && aiInstruction.trim()
                      ? 'bg-accent-500 hover:bg-accent-400 text-white'
                      : 'bg-ink-800 text-ink-500 cursor-not-allowed'
                  }`}
                >
                  {isAiLoading ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {aiEditMode === 'multi' ? 'Editing frontend...' : 'Thinking...'}</>
                  ) : aiEditMode === 'multi' ? 'Apply AI Edit (All Frontend)' : `Apply AI Edit to ${displayName(selectedFile)}`}
                </button>

                <div className="border-t border-ink-800 pt-4">
                  <p className="text-xs text-ink-500 font-medium mb-2">Quick prompts</p>
                  {(aiEditMode === 'multi' ? [
                    'Make all pages use a bold dark palette with gold accents',
                    'Add a sticky announcement banner at the top of every page',
                    'Redesign the product page with a magazine-style layout',
                    'Make the collection grid larger with better product cards',
                    'Rewrite the About page with compelling brand story copy',
                    'Add a newsletter signup section to the home page footer',
                    'Improve mobile responsiveness across all pages',
                  ] : [
                    'Make the hero section full-screen with a bold background',
                    'Change the color palette to be more luxurious and minimal',
                    'Add social media icons to the footer',
                    'Make the typography more elegant with a serif font',
                    'Increase contrast for better readability',
                  ]).map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => setAiInstruction(prompt)}
                      className="w-full text-left text-xs text-ink-500 hover:text-ink-300 py-1 transition-colors"
                    >
                      → {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Replicate Tab ── */}
            {rightTab === 'replicate' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <p className="text-xs text-ink-500 leading-relaxed">
                  Enter an inspiration website URL. The editor will extract its colors, fonts, and layout, then use them to regenerate this storefront.
                </p>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-ink-400">Inspiration URL</label>
                  <input
                    type="url"
                    className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-ink-200 placeholder-ink-600 focus:outline-none focus:border-violet-500 transition-colors"
                    placeholder="https://example.com"
                    value={replicateUrl}
                    onChange={(e) => setReplicateUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && replicateUrl.trim() && !isScraping) handleScrape();
                    }}
                  />
                </div>

                <button
                  onClick={handleScrape}
                  disabled={isScraping || !replicateUrl.trim()}
                  className={`w-full text-sm py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    !isScraping && replicateUrl.trim()
                      ? 'bg-violet-600 hover:bg-violet-500 text-white'
                      : 'bg-ink-800 text-ink-500 cursor-not-allowed'
                  }`}
                >
                  {isScraping ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Extracting...</>
                  ) : 'Extract Style'}
                </button>

                {scrapeError && (
                  <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2">
                    {scrapeError}
                  </p>
                )}

                {scrapeResult && (
                  <div className="space-y-3">
                    <div className="rounded-lg overflow-hidden border border-ink-700">
                      <img
                        src={`data:image/png;base64,${scrapeResult.screenshot}`}
                        alt="Site preview"
                        className="w-full object-cover"
                        style={{ maxHeight: '120px', objectPosition: 'top' }}
                      />
                    </div>

                    <div className="bg-ink-900 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-ink-400">Extracted</p>
                      {scrapeResult.extractedColors.length > 0 && (
                        <div>
                          <p className="text-xs text-ink-500 mb-1">Colors</p>
                          <div className="flex flex-wrap gap-1">
                            {scrapeResult.extractedColors.slice(0, 6).map((color) => (
                              <span key={color} className="w-5 h-5 rounded border border-ink-700 flex-shrink-0" style={{ backgroundColor: color }} title={color} />
                            ))}
                          </div>
                        </div>
                      )}
                      {scrapeResult.extractedFonts.length > 0 && (
                        <div>
                          <p className="text-xs text-ink-500 mb-1">Fonts</p>
                          <div className="flex flex-wrap gap-1">
                            {scrapeResult.extractedFonts.slice(0, 3).map((font) => (
                              <span key={font} className="text-xs bg-ink-800 text-ink-300 rounded px-1.5 py-0.5 truncate max-w-full">{font}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-3 text-xs text-ink-500">
                        <span>{scrapeResult.layoutSections} sections</span>
                        <span>Nav: {scrapeResult.navStyle}</span>
                      </div>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                      <p className="text-xs text-amber-300 mb-2">
                        Style extracted. Regenerating will create a new draft using this aesthetic — your current edits will be overwritten.
                      </p>
                      <button
                        onClick={() => navigate(`/admin/websites/${influencerDbId}?openGenerate=true`)}
                        className="w-full text-sm py-2 rounded-lg font-medium transition-colors bg-amber-600 hover:bg-amber-500 text-white"
                      >
                        Regenerate with this Style →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Panel>
      </Group>

      {/* ── Unsaved changes modal ── */}
      {showLeaveModal && (
        <UnsavedModal
          onStay={() => setShowLeaveModal(false)}
          onLeave={() => {
            setShowLeaveModal(false);
            if (pendingNavRef.current) navigate(pendingNavRef.current);
          }}
        />
      )}
    </div>
  );
}
